/**
 * Detects a provably-empty intersection between a draft `FilterExpression`
 * and a locked one on the same property — the mechanical check behind
 * `FilterConflictNoticeMolecule` (EAINT-781).
 *
 * Scope is deliberately narrow: same property, both clauses use an
 * equality/set-membership operator (`equals`, `not_equals`, `in`, `not_in`).
 * Range/date-window contradictions are NOT detected — deciding those
 * properly needs an interval algebra over `FilterExpression`, which is its
 * own piece of work, and a wrong heuristic here trains users to ignore real
 * warnings. See spec § UI Components, "Conflict detection scope."
 */
import type {
  ArcFilter,
  ArcFilterLeaf,
  FilterExpression,
  FilterGroup,
  PerspectiveFilterLeaf,
  RelationshipFilter,
  RelationshipFilterLeaf,
} from 'types/filters';
import type { FilterLayer } from 'types/contextView';
import { isLaneLeaf, isPropertyLeaf } from '@center-platform/spheres/filters';

type EqualitySetOperator = 'equals' | 'not_equals' | 'in' | 'not_in';

function isEqualitySetOperator(operator: string): operator is EqualitySetOperator {
  return operator === 'equals' || operator === 'not_equals' || operator === 'in' || operator === 'not_in';
}

function isPositive(operator: EqualitySetOperator): boolean {
  return operator === 'equals' || operator === 'in';
}

function admissibleValues(clause: FilterExpression): unknown[] {
  if (Array.isArray(clause.values)) return clause.values;
  if (clause.value !== undefined && clause.value !== null) return [clause.value];
  return [];
}

/**
 * True when `locked` and `draft` cannot both match any sphere — a provably
 * empty intersection, not a heuristic guess. `false` (never warn) for any
 * operator pair outside the decidable equality/set-membership scope above.
 */
export function clausesConflict(locked: FilterExpression, draft: FilterExpression): boolean {
  if (locked.property !== draft.property) return false;
  if (!isEqualitySetOperator(locked.operator) || !isEqualitySetOperator(draft.operator)) return false;

  const lockedValues = admissibleValues(locked);
  const draftValues = admissibleValues(draft);
  const lockedPositive = isPositive(locked.operator);
  const draftPositive = isPositive(draft.operator);

  if (lockedPositive && draftPositive) {
    // Both admit a finite set — conflict iff the sets don't overlap.
    return lockedValues.length > 0 && !lockedValues.some((v) => draftValues.includes(v));
  }
  if (lockedPositive && !draftPositive) {
    // locked admits {lockedValues}; draft excludes {draftValues}. Conflict
    // iff draft excludes every value locked admits.
    return lockedValues.length > 0 && lockedValues.every((v) => draftValues.includes(v));
  }
  if (!lockedPositive && draftPositive) {
    return draftValues.length > 0 && draftValues.every((v) => lockedValues.includes(v));
  }
  // Both exclusion-shaped (not_equals/not_in on each side) — excluding two
  // different values can't be proven disjoint in general; not decidable.
  return false;
}

/** The first locked clause (from `lockedClauses`) the draft clause conflicts with, if any. */
export function findConflictingClause(
  draft: FilterExpression,
  lockedClauses: FilterExpression[]
): FilterExpression | undefined {
  return lockedClauses.find((locked) => clausesConflict(locked, draft));
}

/** A locked clause plus the tier (context/view/user/session/link) it came from. */
export interface LockedClause {
  clause: FilterExpression;
  tier: FilterLayer;
}

/** A locked lane clause plus the tier it came from — see `LaneValueClause`. */
export interface LockedLaneClause {
  clause: LaneValueClause;
  tier: FilterLayer;
}

/**
 * Walks every property leaf in `draft` (including nested `groups`, and
 * identity leaves such as `type`/`label`) and reports the first locked
 * clause each one conflicts with, keyed by leaf id — so a narrow host can
 * pass `problemOf` per row instead of one panel-level notice. Leaves without
 * an `id` are skipped: they carry no id to key by. Lane leaves
 * (`kind: 'perspective' | 'relationship' | 'arc'`) are compared against
 * `lockedLane` (SC7) when the caller supplies it; omitted entirely, they are
 * skipped, matching the property-only behaviour every existing caller relies on.
 */
export function findConflictsByLeaf(draft: FilterGroup, locked: LockedClause[]): Map<string, LockedClause>;
export function findConflictsByLeaf(
  draft: FilterGroup,
  locked: LockedClause[],
  lockedLane: LockedLaneClause[]
): Map<string, LockedClause | LockedLaneClause>;
export function findConflictsByLeaf(
  draft: FilterGroup,
  locked: LockedClause[],
  lockedLane: LockedLaneClause[] = []
): Map<string, LockedClause | LockedLaneClause> {
  const result = new Map<string, LockedClause | LockedLaneClause>();
  const lockedFilters = locked.map((entry) => entry.clause);
  const lockedLaneFilters = lockedLane.map((entry) => entry.clause);

  function walk(group: FilterGroup): void {
    group.expressions.forEach((leaf) => {
      if (isPropertyLeaf(leaf)) {
        if (!leaf.id) return;
        const found = findConflictingClause(leaf, lockedFilters);
        if (!found) return;
        const owner = locked.find((entry) => entry.clause === found);
        if (owner) result.set(leaf.id as string, owner);
        return;
      }
      if (isLaneLeaf(leaf)) {
        const id = leaf.filter.id;
        if (!id) return;
        const found = findConflictingLaneClause(leaf, lockedLaneFilters);
        if (!found) return;
        const owner = lockedLane.find((entry) => entry.clause === found);
        if (owner) result.set(id, owner);
      }
    });
    (group.groups ?? []).forEach(walk);
  }

  walk(draft);
  return result;
}

/**
 * A same-lane, same-target predicate reduced to operator + admissible value
 * set — the shape the approved SC7 conflict rule (2026-09-15) compares. An
 * `ArcFilter` yields one clause per predicate axis it sets (`stageId`,
 * `healthStatus`, `lifecycle`), each keyed by its own `target` so a stage
 * contradiction is never compared against a health contradiction.
 */
export interface LaneValueClause {
  lane: 'perspective' | 'relationship' | 'arc';
  /** perspectiveSystemName / relationshipSystemName / arc-template+selector+axis key. */
  target: string;
  operator: 'in' | 'not_in';
  /** Empty = the lane's "any value" form: `in` → at least one edge, `not_in` → no edge at all. */
  values: unknown[];
  /**
   * Relationship lane only — which end of the edge the filtered sphere is on
   * (`RelationshipFilter.direction`, default `'both'`): `'source'` reads its
   * outgoing edges, `'target'` its incoming ones, `'both'` either
   * (`filterEvaluation.ts` `evaluateRelationshipLeafSync`). Absent on the
   * perspective and arc lanes, which have no direction.
   */
  direction?: RelationshipDirection;
}

type RelationshipDirection = NonNullable<RelationshipFilter['direction']>;

function arcAxisClauses(filter: ArcFilter): LaneValueClause[] {
  const arcTarget = `${filter.templateSystemName ?? '*'}:${filter.arcSelector ?? 'any'}`;
  const axes: { key: string; values: unknown[] }[] = [];
  if (filter.stageId?.length) {
    axes.push({ key: 'stage', values: filter.stageId });
  }
  if (filter.healthStatus?.length) {
    axes.push({ key: 'health', values: filter.healthStatus });
  }
  if (filter.lifecycle?.length) {
    axes.push({ key: 'lifecycle', values: filter.lifecycle });
  }

  // An `ArcFilter`'s axes are correlated on ONE arc (`types/filters.d.ts`):
  // `in {stageId:[s], healthStatus:[h]}` means "an arc that is both s and h".
  // Splitting that into independent per-axis clauses yields a WEAKER
  // constraint, so a contradiction proven against one part also holds against
  // the whole — sound. The same split on `not_in` yields a STRONGER one
  // ("no arc that is both s and h" would become "no arc in stage s"), which
  // invents contradictions the filter never asserted, so a multi-axis `not_in`
  // offers nothing decidable to compare. A single-axis `not_in` is exact.
  if (filter.operator === 'not_in' && axes.length > 1) return [];

  return axes.map(({ key, values }) => ({
    lane: 'arc' as const,
    target: `${arcTarget}:${key}`,
    operator: filter.operator,
    values,
  }));
}

/**
 * An `in` whose `minCount` is below 1 is satisfied by a sphere with no
 * matching edge at all, so it constrains nothing and can contradict nothing.
 */
function isVacuousAdmission(filter: { operator: 'in' | 'not_in'; minCount?: number }): boolean {
  return filter.operator === 'in' && filter.minCount !== undefined && filter.minCount < 1;
}

/**
 * Every comparable `LaneValueClause` a lane leaf carries (0 for an arc
 * existence check with no axis to compare, or a vacuous `minCount` < 1 `in`).
 */
export function laneValueClauses(
  leaf: PerspectiveFilterLeaf | RelationshipFilterLeaf | ArcFilterLeaf
): LaneValueClause[] {
  if (leaf.kind === 'perspective') {
    if (isVacuousAdmission(leaf.filter)) return [];
    return [{ lane: 'perspective', target: leaf.filter.perspectiveSystemName, operator: leaf.filter.operator, values: leaf.filter.assignees ?? [] }];
  }
  if (leaf.kind === 'relationship') {
    if (isVacuousAdmission(leaf.filter)) return [];
    return [
      {
        lane: 'relationship',
        target: leaf.filter.relationshipSystemName,
        operator: leaf.filter.operator,
        values: leaf.filter.relatedTo ?? [],
        direction: leaf.filter.direction ?? 'both',
      },
    ];
  }
  if (isVacuousAdmission(leaf.filter)) return [];
  return arcAxisClauses(leaf.filter);
}

/**
 * Whether the excluding clause rules out every edge the admitting clause could
 * be satisfied through. Lanes without a direction always cover. On the
 * relationship lane a `'both'` exclusion covers either end; otherwise the ends
 * must match — `in` on `'source'` and `not_in` on `'target'` read disjoint
 * edge sets, and an `in` on `'both'` can still be met through the end a
 * one-sided exclusion leaves open.
 */
function exclusionCoversAdmission(admits: LaneValueClause, excludes: LaneValueClause): boolean {
  if (admits.lane !== 'relationship') return true;
  const admitsDirection = admits.direction ?? 'both';
  const excludesDirection = excludes.direction ?? 'both';
  return excludesDirection === 'both' || excludesDirection === admitsDirection;
}

/**
 * True when two same-lane, same-target clauses cannot both hold — the same
 * provably-empty-intersection rule `clausesConflict` applies to properties,
 * read over a lane's value set.
 *
 * A lane value set is disjunctive: a perspective `in [u1, u2]` matches a
 * sphere holding EITHER assignee (`types/filters.d.ts` — `assignees` is a
 * list, `minCount` defaults to an exists check). So an overlap between the
 * two sides proves nothing: `in [u1, u2]` and `not_in [u2]` are both
 * satisfied by a sphere assigned only to `u1`. The pair is unsatisfiable only
 * when the excluding side rules out EVERY value the admitting side allows —
 * containment, not overlap.
 *
 * An empty excluding set is "no edge at all" and rules out every admitting
 * clause, including a pure existence check. An empty admitting set ("has any
 * assignee") against a non-empty exclusion stays satisfiable through any
 * other value, so it is not a conflict.
 *
 * On the relationship lane the exclusion must also cover the admitting side's
 * direction — see `exclusionCoversAdmission`.
 *
 * Same-polarity pairs (`in`/`in`, `not_in`/`not_in`) are never a conflict: a
 * lane target can legitimately narrow across more than one authored clause.
 */
export function laneClausesConflict(a: LaneValueClause, b: LaneValueClause): boolean {
  if (a.lane !== b.lane || a.target !== b.target) return false;
  if (a.operator === b.operator) return false;

  const admits = a.operator === 'in' ? a : b;
  const excludes = a.operator === 'in' ? b : a;
  if (!exclusionCoversAdmission(admits, excludes)) return false;
  if (excludes.values.length === 0) return true;
  return admits.values.length > 0 && admits.values.every((v) => excludes.values.includes(v));
}

/** The first locked lane clause a draft lane leaf conflicts with, if any. */
export function findConflictingLaneClause(
  draftLeaf: PerspectiveFilterLeaf | RelationshipFilterLeaf | ArcFilterLeaf,
  lockedLaneClauses: LaneValueClause[]
): LaneValueClause | undefined {
  const draftClauses = laneValueClauses(draftLeaf);
  for (const draftClause of draftClauses) {
    const found = lockedLaneClauses.find((locked) => laneClausesConflict(locked, draftClause));
    if (found) return found;
  }
  return undefined;
}
